/**
 * megaRosterOverrides — auditable patch layer for the Mega dex roster.
 *
 * WHY THIS EXISTS
 * ---------------
 * The Mega dex roster (MEGA_DEX_IDS in pogoAvailability.ts) is derived from
 * the curated sprite tables in megaGmaxSprites.ts, whose core is kept in step
 * with PoGoAPI's mega feed (https://pogoapi.net/api/v1/mega_pokemon.json) via
 * the weekly audit in scripts/generate-mega-roster.ts.
 *
 * PoGoAPI is the primary automated signal, but it LAGS the live game: when
 * Niantic debuts a new Mega (often through Super Mega Raids) the species can be
 * catchable in-game for weeks before PoGoAPI's feed lists it. During that gap
 * the Mega dex would otherwise be missing a species that players can already
 * obtain. This file is the small, reviewable bridge for exactly that gap: each
 * entry is an announced-but-not-yet-in-PoGoAPI Mega, carrying its source URLs,
 * a reason, and review/expiry metadata so a human can re-check it on schedule.
 *
 * It is data, not code: adding a future Mega means appending one object here,
 * never editing logic. The audit script flags entries that PoGoAPI has since
 * caught up on (so they can be removed) and entries past their `expiresAt`
 * (so a stale override never silently lingers).
 *
 * WHY "SUPER MEGA" / "SUPER MAX" IS NOT A SEPARATE DEX
 * ---------------------------------------------------
 * Pokémon GO exposes no distinct Pokédex page for Super Mega Raids or the
 * Super Max Mega Level (Bulbapedia frames Super Max as a fourth *Mega Level*,
 * and Nintendo Wire frames Super Mega Raids as a *raid/event type* — neither is
 * a Pokédex category). A species that reaches Super Max Level is still a Mega
 * Evolution and belongs in the existing Mega dex. We therefore represent the
 * provenance as a `megaLevel` source tag on the override, NOT as a new dex
 * variant. If GO ever ships a dedicated Super Mega Pokédex page, that is the
 * point to revisit — until then, a new dex type would be churn with no product
 * meaning and would split a single roster across two tabs.
 */
import { isReleasedAtLocal } from "@/lib/releaseAtLocal";

/**
 * The Mega Level the override entry's provenance came from. This is a SOURCE
 * TAG only — it does not change which dex the species belongs to (always the
 * Mega dex). "mega" is the ordinary Mega; "super_max" is the Feb-2026 fourth
 * Mega Level; "super_mega_raid" marks a debut that came via a Super Mega Raid.
 */
export type MegaLevelTag = "mega" | "super_max" | "super_mega_raid";

export interface MegaRosterOverride {
  /** Base National Dex id (matches speciesNames.ts / silhouette atlas). */
  readonly baseId: number;
  /** Representative pokesprite slug, mirrors megaGmaxSprites MEGA_SLUG_BY_ID. */
  readonly slug: string;
  /** PokeAPI FORM pokemon id for high-res art (official-artwork / home). */
  readonly formId: number;
  /** Provenance tag; does not affect dex membership (always the Mega dex). */
  readonly megaLevel: MegaLevelTag;
  /** Short human reason the override exists (why PoGoAPI is insufficient). */
  readonly reason: string;
  /** Citations a reviewer can open to re-verify the entry. */
  readonly sources: readonly string[];
  /** ISO date this entry was last verified against the sources. */
  readonly reviewedAt: string;
  /**
   * ISO date after which this override must be re-reviewed. Past this date the
   * audit script (scripts/generate-mega-roster.ts) warns so the entry is either
   * renewed or removed once PoGoAPI catches up. Keeps the patch layer honest.
   */
  readonly expiresAt: string;
  /**
   * Optional NAIVE local wall-clock release ("2026-09-05T10:00", no zone
   * suffix). GO events start at local time in every player's own timezone
   * (docs/event-time-zones.md), so the gate resolves this string on the
   * DEVICE clock — `new Date("…T10:00")` parses in the device zone, which is
   * exactly the event semantics. Until that moment the entry is excluded
   * from the merged Mega tables (roster + art), so a pre-shipped app build
   * shows the new cells only once the event actually starts for that player.
   * Absent = released immediately (every pre-existing entry).
   *
   * Evaluated when the sprite tables are built (app launch), matching how
   * the rest of the curated rosters snapshot — a session left open across
   * the boundary picks the new Megas up on its next launch.
   */
  readonly releaseAtLocal?: string;
}

/**
 * Has this override's local release moment passed on the given clock?
 * A malformed date must never hide a Mega — parse failure counts as released.
 */
export function isMegaOverrideReleased(
  o: MegaRosterOverride,
  now: Date = new Date(),
): boolean {
  return isReleasedAtLocal(o.releaseAtLocal, now);
}

/**
 * Announced-but-missing Mega entries not yet in the PoGoAPI mega feed.
 *
 * Re-verified 2026-08-24 against the live feed (npm run generate:mega):
 * PoGoAPI's mega_pokemon.json still lists 47 base species and does NOT include
 * any of the entries below (OVERLAY_PROMOTABLE: none). PokeAPI DOES have
 * high-res official-artwork for each form id here, so the runtime art resolves
 * at premium quality with no pixel-art fallback needed.
 *
 * Note: Mewtwo (#150) is intentionally NOT here — it is already a long-standing
 * member of the curated core table even though PoGoAPI's feed omits it, which
 * is itself proof that PoGoAPI is a lagging signal rather than the canonical
 * roster. Mega Mewtwo X/Y availability via GO Fest 2026 Super Mega Raids is
 * cited on the core entry, not duplicated here.
 */
export const MEGA_ROSTER_OVERRIDES: readonly MegaRosterOverride[] = [
  {
    // Mega Chandelure: Pokémon GO debut at the Chandelure Super Mega Raid Day
    // (2026-10-31, 2 to 5 pm local, LeekDuck, read 2026-10-20), shiny
    // boosted, Mega Level 1 unlocked on the catch. A Legends: Z-A Mega, so
    // PoGoAPI's feed lags it the way it lagged Dragonite and Victreebel.
    // PokeAPI form 10291 (chandelure-mega). No release gate (owner rule
    // 2026-10-09: upcoming cells are open from the day they are added).
    baseId: 609,
    slug: "chandelure-mega",
    formId: 10291,
    megaLevel: "super_mega_raid",
    reason:
      "Mega Chandelure debuts in Super Mega Raids on 2026-10-31 (LeekDuck); " +
      "a Legends: Z-A Mega not yet in PoGoAPI's mega feed.",
    sources: [
      "https://leekduck.com/events/chandelure-super-mega-raid-day-2026/",
      "https://bulbapedia.bulbagarden.net/wiki/Mega_Evolution_(GO)",
    ],
    reviewedAt: "2026-10-20",
    expiresAt: "2027-01-20",
  },
  {
    baseId: 71,
    slug: "victreebel-mega",
    formId: 10279,
    megaLevel: "super_max",
    reason:
      "Bulbapedia lists Victreebel among the species that can reach the new " +
      "Super Max Mega Level (introduced 2026-02-20); not yet in PoGoAPI mega feed.",
    sources: [
      "https://bulbapedia.bulbagarden.net/wiki/Mega_Evolution_(GO)",
    ],
    reviewedAt: "2026-08-24",
    expiresAt: "2026-11-24",
  },
  {
    baseId: 149,
    slug: "dragonite-mega",
    formId: 10281,
    megaLevel: "super_max",
    reason:
      "Bulbapedia lists Dragonite among the species that can reach the new " +
      "Super Max Mega Level (introduced 2026-02-20); not yet in PoGoAPI mega feed.",
    sources: [
      "https://bulbapedia.bulbagarden.net/wiki/Mega_Evolution_(GO)",
    ],
    reviewedAt: "2026-08-24",
    expiresAt: "2026-11-24",
  },
  {
    baseId: 687,
    slug: "malamar-mega",
    formId: 10297,
    megaLevel: "super_max",
    reason:
      "Bulbapedia lists Malamar among the species that can reach the new " +
      "Super Max Mega Level (introduced 2026-02-20); not yet in PoGoAPI mega feed.",
    sources: [
      "https://bulbapedia.bulbagarden.net/wiki/Mega_Evolution_(GO)",
    ],
    reviewedAt: "2026-08-24",
    expiresAt: "2026-11-24",
  },
  {
    baseId: 870,
    slug: "falinks-mega",
    formId: 10303,
    megaLevel: "super_mega_raid",
    reason:
      "Mega Falinks debuted via Super Mega Raids and can reach Super Max " +
      "Level (Nintendo Wire + Bulbapedia); not yet in PoGoAPI mega feed.",
    sources: [
      "https://nintendowire.com/guides/pokemon-go/season-of-memories-in-motion/falinks-super-mega-raid-day/",
      "https://bulbapedia.bulbagarden.net/wiki/Mega_Evolution_(GO)",
    ],
    reviewedAt: "2026-08-24",
    expiresAt: "2026-11-24",
  },
  {
    baseId: 227,
    slug: "skarmory-mega",
    formId: 10284,
    megaLevel: "super_mega_raid",
    reason:
      "Mega Skarmory (Steel/Flying) debuted in Pokémon GO via a Super Mega " +
      "Raid; PokeAPI form id 10284 ships high-res art, but PoGoAPI's mega feed " +
      "does not list it yet.",
    sources: [
      "https://pokeapi.co/api/v2/pokemon/skarmory-mega",
      "https://www.dittobase.com/pokemon-go/pokedex/skarmory-mega",
    ],
    reviewedAt: "2026-07-15",
    expiresAt: "2026-10-15",
  },
  {
    baseId: 26,
    slug: "raichu-mega-x",
    formId: 10304,
    megaLevel: "super_mega_raid",
    reason:
      "Mega Raichu debuted 2026-07-18 via Raichu Super Mega Raid Day. It is an " +
      "X/Y PAIR, GO's own Raichu dex screen shows separate MEGA X and MEGA Y " +
      "cells, so this entry carries the X (representative base cell) and the Y " +
      "lives in MEGA_EXTRA_FORMS. Bulbapedia now lists both forms (separate " +
      "Mega Energy since 2026-05-18) but PoGoAPI's mega feed still lacks " +
      "Raichu; PokeAPI has both forms with high-res art (10304 / 10305).",
    sources: [
      "https://leekduck.com/events/raichu-super-mega-raid-day-2026/",
      "https://bulbapedia.bulbagarden.net/wiki/Mega_Evolution_(GO)",
      "https://pokeapi.co/api/v2/pokemon/raichu-mega-x",
      "https://pokeapi.co/api/v2/pokemon/raichu-mega-y",
    ],
    reviewedAt: "2026-08-24",
    // Renewed post-release 2026-08-24: the raid day has come and gone but the
    // PoGoAPI feed still lists 47 species with no Raichu. Once it catches up,
    // generate-mega-roster.ts reports OVERLAY_PROMOTABLE and this entry goes.
    expiresAt: "2026-11-24",
  },
  // ── GO Fest 2026: Mega Finale (2026-09-05/06) — the Kalos starter Megas ──
  // Shipped ungated on 2026-08-31 (owner call): the three cells are live in
  // the Mega dex now, ahead of the event, rather than waiting on a
  // `releaseAtLocal` gate that needed an app-store build to land first.
  // PokeAPI ships full high-res art for all three form ids (official-artwork
  // + home, shiny included), verified 2026-08-24; R2 + the silhouette atlas
  // already carry them.
  {
    baseId: 652,
    slug: "chesnaught-mega",
    formId: 10292,
    megaLevel: "mega",
    reason:
      "Mega Chesnaught debuts at GO Fest 2026: Mega Finale (2026-09-05); " +
      "announced by Scopely, not yet in the Game Master or PoGoAPI mega feed.",
    sources: [
      "https://leekduck.com/events/pokemon-go-fest-2026-mega-finale/",
      "https://pokemongohub.net/post/news/pokemon-go-fest-2026-mega-finale-teaser-shows-new-megas/",
      "https://pokeapi.co/api/v2/pokemon/chesnaught-mega",
    ],
    reviewedAt: "2026-08-24",
    expiresAt: "2026-11-05",
  },
  {
    baseId: 655,
    slug: "delphox-mega",
    formId: 10293,
    megaLevel: "mega",
    reason:
      "Mega Delphox debuts at GO Fest 2026: Mega Finale (2026-09-05); " +
      "announced by Scopely, not yet in the Game Master or PoGoAPI mega feed.",
    sources: [
      "https://leekduck.com/events/pokemon-go-fest-2026-mega-finale/",
      "https://pokemongohub.net/post/news/pokemon-go-fest-2026-mega-finale-teaser-shows-new-megas/",
      "https://pokeapi.co/api/v2/pokemon/delphox-mega",
    ],
    reviewedAt: "2026-08-24",
    expiresAt: "2026-11-05",
  },
  {
    baseId: 658,
    slug: "greninja-mega",
    formId: 10294,
    megaLevel: "mega",
    reason:
      "Mega Greninja debuts at GO Fest 2026: Mega Finale (2026-09-05); " +
      "announced by Scopely, not yet in the Game Master or PoGoAPI mega feed.",
    sources: [
      "https://leekduck.com/events/pokemon-go-fest-2026-mega-finale/",
      "https://pokemongohub.net/post/news/pokemon-go-fest-2026-mega-finale-teaser-shows-new-megas/",
      "https://pokeapi.co/api/v2/pokemon/greninja-mega",
    ],
    reviewedAt: "2026-08-24",
    expiresAt: "2026-11-05",
  },
  // ── Mega Ascension (live 2026-09-03) ──
  // Added to the Mega dex through the admin Dex Config portal first, which
  // reaches the roster and the colour-art resolver but NOT the build-time
  // layer: FORM_SOURCES and the shipped silhouette atlas are bundled assets,
  // so a portal-only Mega has no silhouette tile and every uncaught cell
  // renders blank. It also missed megaLevels (speciesHasMega reads the bundled
  // table), so the species page had no Mega view and event tiles read
  // "Starmie" instead of "Mega Starmie". Promoting the entry here is what the
  // overlay is for; the portal row stays and simply agrees with it.
  {
    baseId: 121,
    slug: "starmie-mega",
    formId: 10280,
    megaLevel: "mega",
    reason:
      "Mega Starmie is live in the Mega Ascension event (added to the Mega dex " +
      "via the admin Dex Config portal on 2026-09-03); not in PoGoAPI's mega " +
      "feed. PokeAPI carries the form with high-res art (10280).",
    sources: [
      "https://pokeapi.co/api/v2/pokemon/starmie-mega",
      "https://bulbapedia.bulbagarden.net/wiki/Mega_Evolution_(GO)",
    ],
    reviewedAt: "2026-09-03",
    expiresAt: "2026-12-03",
  },
  // ── Staraptor Super Mega Raid Day (2026-09-19, 14:00-17:00 local) ──
  // Mega Staraptor debuts in Super Mega Raids and can reach Super Max level;
  // Brave Bird+ is its Mega-only Charged Attack. Not gated, following the
  // Kalos-starter call (dc46950e): the cell goes live ahead of the event so
  // the roster, art and silhouette are all on device before the day. The
  // PokeMiners GAME_MASTER export (checked 2026-09-07) does not carry its
  // tempEvoOverrides or Brave Bird+ yet, so megaStats / megaMoves pick it up
  // on their next regen once Niantic pushes the templates.
  {
    baseId: 398,
    slug: "staraptor-mega",
    formId: 10308,
    megaLevel: "super_mega_raid",
    reason:
      "Mega Staraptor debuts on Staraptor Super Mega Raid Day, 2026-09-19 " +
      "(LeekDuck event page); not in PoGoAPI's mega feed. PokeAPI carries the " +
      "form with high-res art (10308).",
    sources: [
      "https://leekduck.com/events/staraptor-super-mega-raid-day-2026/",
      "https://pokeapi.co/api/v2/pokemon/staraptor-mega",
    ],
    reviewedAt: "2026-09-07",
    expiresAt: "2026-12-07",
  },
];

/** baseId -> override, for cheap lookup when merging into the sprite tables. */
export const MEGA_ROSTER_OVERRIDES_BY_ID: ReadonlyMap<number, MegaRosterOverride> =
  new Map(MEGA_ROSTER_OVERRIDES.map((o) => [o.baseId, o]));
